// Kjuup Pro task UI pieces for a project's Aufgaben (port of the mobile ToDoProParts):
// task row, section header, plan card (All / My day switch, week
// strip, footer), tags, avatars, sheets and small data hooks. Presentation only: ToDo.js and
// the task panels own every request that changes a task and hand the handlers in.
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import MaterialIcon from "../../components/MaterialIcon";
import LanguageContext from "../../helper/LanguageContext";
import { callAPI } from "../../helper/apiUtils";
import { initialsOf, roomCodeOf } from "./todoProModel";
import "./ToDoPro.css";

/* ---- Text ----------------------------------------------------------------------- */

/**
 * `t(key, en, de)` = strings[key] || (isGerm ? de : en): the other language files have no
 * fallback chain, so every label carries its English and German default.
 */
const NO_STRINGS = {};

export function useTodoText() {
  const ctx = useContext(LanguageContext) || NO_STRINGS;
  const strings = ctx.strings || NO_STRINGS;
  const isGerm = !!ctx.isGerm;
  const t = useCallback((key, en, de) => strings[key] || (isGerm ? de : en), [strings, isGerm]);
  return { t, strings, isGerm, language: ctx.language };
}

/** false until `ms` have passed (skeletons only show on slow loads). */
export function useShowAfter(ms = 250) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setShow(true), ms);
    return () => clearTimeout(id);
  }, [ms]);
  return show;
}

/* ---- Data (shared, cached briefly) ------------------------------------------------- */

const CACHE_MS = 2 * 60 * 1000;
const cache = { rooms: null, roomsAt: 0, roomsPromise: null, users: null, usersAt: 0, usersPromise: null };

/** The user's rooms (GET /building/my, with isJoin). Cached for two minutes. */
export function loadMyRooms(force = false) {
  if (!force && cache.rooms && Date.now() - cache.roomsAt < CACHE_MS) return Promise.resolve(cache.rooms);
  if (cache.roomsPromise) return cache.roomsPromise;
  cache.roomsPromise = callAPI("/building/my", {}, "GET")
    .then((res) => {
      const data = (res && res.data) || {};
      let list = [];
      if (Array.isArray(data.buildings)) list = data.buildings;
      else if (data.data && Array.isArray(data.data.buildings)) list = data.data.buildings;
      else if (Array.isArray(data.data)) list = data.data;
      cache.rooms = list.filter((room) => room && room.id != null && room.id !== "");
      cache.roomsAt = Date.now();
      return cache.rooms;
    })
    .catch(() => cache.rooms || [])
    .finally(() => {
      cache.roomsPromise = null;
    });
  return cache.roomsPromise;
}

export function useMyRooms({ enabled = true } = {}) {
  const [rooms, setRooms] = useState(() => cache.rooms || []);
  const [loading, setLoading] = useState(() => enabled && !cache.rooms);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    if (!cache.rooms) setLoading(true);
    loadMyRooms().then((list) => {
      if (!alive) return;
      setRooms(list || []);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [enabled]);
  return { rooms, loading };
}

/** Everyone the user can assign (GET /user/allUsersSearch, first 100). Cached for two minutes. */
export function loadAllUsers(force = false) {
  if (!force && cache.users && Date.now() - cache.usersAt < CACHE_MS) return Promise.resolve(cache.users);
  if (cache.usersPromise) return cache.usersPromise;
  cache.usersPromise = callAPI("/user/allUsersSearch?limit=100&page=1&query=", {}, "GET")
    .then((res) => {
      const data = (res && res.data) || {};
      const list = (data.data && Array.isArray(data.data.users) && data.data.users) || (Array.isArray(data.users) && data.users) || [];
      cache.users = list.filter((user) => user && user.id != null && user.id !== "");
      cache.usersAt = Date.now();
      return cache.users;
    })
    .catch(() => cache.users || [])
    .finally(() => {
      cache.usersPromise = null;
    });
  return cache.usersPromise;
}

export function useAllUsers({ enabled = true } = {}) {
  const [users, setUsers] = useState(() => cache.users || []);
  const [loading, setLoading] = useState(() => enabled && !cache.users);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    if (!cache.users) setLoading(true);
    loadAllUsers().then((list) => {
      if (!alive) return;
      setUsers(list || []);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [enabled]);
  return { users, loading };
}

/** Debounced people search with paging (detail "Add people"). Empty query = no results. */
export function usePeopleSearch(query, { pageSize = 20, enabled = true } = {}) {
  const [state, setState] = useState({ results: [], loading: false, hasMore: false, loadingMore: false });
  const live = useRef({ q: "", page: 1 });
  useEffect(() => {
    const q = enabled ? String(query || "").trim() : "";
    live.current = { q, page: 1 };
    if (!q) {
      setState({ results: [], loading: false, hasMore: false, loadingMore: false });
      return undefined;
    }
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    const id = setTimeout(async () => {
      try {
        const res = await callAPI(`/user/allUsersSearch?limit=${pageSize}&page=1&query=${encodeURIComponent(q)}`, {}, "GET");
        const users = (res && res.data && res.data.data && res.data.data.users) || [];
        if (alive && live.current.q === q) setState({ results: users, loading: false, hasMore: users.length === pageSize, loadingMore: false });
      } catch (e) {
        if (alive) setState({ results: [], loading: false, hasMore: false, loadingMore: false });
      }
    }, 300);
    return () => {
      alive = false;
      clearTimeout(id);
    };
  }, [query, pageSize, enabled]);

  const loadMore = useCallback(async () => {
    const { q, page } = live.current;
    if (!q) return;
    setState((s) => ({ ...s, loadingMore: true }));
    try {
      const next = page + 1;
      const res = await callAPI(`/user/allUsersSearch?limit=${pageSize}&page=${next}&query=${encodeURIComponent(q)}`, {}, "GET");
      const users = (res && res.data && res.data.data && res.data.data.users) || [];
      if (live.current.q !== q) return;
      live.current.page = next;
      setState((s) => {
        const seen = new Set(s.results.map((u) => String(u.id)));
        return { results: [...s.results, ...users.filter((u) => u && !seen.has(String(u.id)))], loading: false, hasMore: users.length === pageSize, loadingMore: false };
      });
    } catch (e) {
      setState((s) => ({ ...s, loadingMore: false, hasMore: false }));
    }
  }, [pageSize]);

  return { ...state, loadMore };
}

/* ---- Small pieces ------------------------------------------------------------------ */

const AVATAR_SIZES = [20, 22, 30, 36, 38];
const avatarSizeClass = (size) => `tdp-avatar--${AVATAR_SIZES.indexOf(size) >= 0 ? size : 30}`;

/** Photo avatar, initials, or a room code tile (kind="room"). */
export function ProAvatar({ name, src, size = 30, kind, code, className = "" }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  const cls = `tdp-avatar ${avatarSizeClass(size)}${className ? ` ${className}` : ""}`;
  if (kind === "room") {
    return (
      <span className={`${cls} tdp-avatar--room`} aria-hidden="true">
        {code || roomCodeOf(name) || <MaterialIcon icon="folder_open" size={Math.round(size * 0.55)} />}
      </span>
    );
  }
  if (src && !broken) return <img className={cls} src={src} alt="" onError={() => setBroken(true)} />;
  const letters = initialsOf(name);
  return (
    <span className={`${cls} tdp-avatar--initials`} aria-hidden="true">
      {letters || <MaterialIcon icon="person" size={Math.round(size * 0.6)} />}
    </span>
  );
}

/** Stacked assignee avatars (max 3, then a "+N" chip). people = [{ key, name, src }]. */
export function AvatarStack({ people = [], max = 3 }) {
  if (!people.length) return null;
  const shown = people.slice(0, max);
  const rest = people.length - shown.length;
  return (
    <span className="tdp-stack" aria-hidden="true">
      {shown.map((person) => (
        <span key={person.key} className="tdp-stack-item" title={person.name}>
          <ProAvatar name={person.name} src={person.src} size={22} />
        </span>
      ))}
      {rest > 0 ? <span className="tdp-stack-item tdp-stack-more">{`+${rest}`}</span> : null}
    </span>
  );
}

/** Status tag. tone: err | warn | ok | info | new | muted; size: sm | md | person. */
export function TodoTag({ label, tone = "new", size = "sm" }) {
  return <span className={`tdp-tag tdp-tag--${tone}${size !== "sm" ? ` tdp-tag--${size}` : ""}`}>{label}</span>;
}

export function ProSpinner({ small }) {
  return <span className={`tdp-spinner${small ? " tdp-spinner--sm" : ""}`} aria-hidden="true" />;
}

/** Search box (search icon, input, clear). */
export function ProSearchField({ value, onChange, placeholder, clearLabel, autoFocus, trailing }) {
  return (
    <div className="tdp-search">
      <MaterialIcon icon="search" size={20} className="tdp-search-icon" />
      <input
        className="tdp-search-input"
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
      />
      {trailing}
      {value ? (
        <button type="button" className="tdp-search-clear" onClick={() => onChange("")} aria-label={clearLabel} title={clearLabel}>
          <MaterialIcon icon="cancel" size={18} fill={1} />
        </button>
      ) : null}
    </div>
  );
}

/* ---- Row ---------------------------------------------------------------------------- */

/**
 * Leading control of a row: the 24px task circle (green with a check when done), a spinner
 * while the status call runs, or the selection circle in My day "Adjust list" / delete mode.
 */
export function TodoCheck({ check, finished, busy, selectMode, selected, selectDisabled, onClick, label, compact }) {
  let inner;
  if (selectMode) {
    inner = (
      <span className={`tdp-circle tdp-circle--select${selected ? ` is-selected tdp-circle--${selectMode}` : ""}${selectDisabled ? " is-disabled" : ""}`}>
        {selected ? <MaterialIcon icon="check" size={16} weight={700} /> : null}
      </span>
    );
  } else if (busy) {
    inner = <ProSpinner />;
  } else {
    const done = finished || check === "done" || check === "confirmed";
    inner = <span className={`tdp-circle${done ? " is-done" : ""}`}>{done ? <MaterialIcon icon="check" size={16} weight={700} /> : null}</span>;
  }
  const tappable = !selectMode && !busy && (check === "open" || check === "done" || check === "confirm");
  const cls = `tdp-check${compact ? " tdp-check--compact" : ""}`;
  if (!tappable) return <span className={cls}>{inner}</span>;
  return (
    <button
      type="button"
      className={`${cls} is-tappable`}
      role="checkbox"
      aria-checked={check === "done"}
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.stopPropagation();
        if (onClick) onClick();
      }}
    >
      {inner}
    </button>
  );
}

/**
 * A bookmark's "Termin zuweisen", right of its "Ohne Datum" (mobile). Opens
 * the browser's day picker right away; the hour is set by the caller. The
 * date input stays invisible under the action and only anchors the picker.
 */
export function AssignDate({ label, busy, onPick }) {
  const inputRef = useRef(null);
  const openPicker = (e) => {
    e.stopPropagation();
    const el = inputRef.current;
    if (!el || busy) return;
    try {
      if (typeof el.showPicker === "function") el.showPicker();
      else el.focus();
    } catch (_) {
      el.focus();
    }
  };
  return (
    <span className="tdp-row-assign" onClick={(e) => e.stopPropagation()}>
      <button type="button" className="tdp-row-assign-btn" disabled={busy} onClick={openPicker}>
        <MaterialIcon icon="edit_calendar" size={16} />
        <span>{label}</span>
      </button>
      <input
        ref={inputRef}
        type="date"
        className="tdp-row-date-input"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          if (e.target.value) onPick(e.target.value);
        }}
      />
    </span>
  );
}

/**
 * A row's calendar button (mobile TaskRow): in the user's calendars (filled, accent) or not; a
 * click adds or removes it. `calendar` = { added, loading, needsDate }.
 */
export function CalendarButton({ calendar, labels = {}, onClick }) {
  const label = calendar.added ? labels.calendarRemove : labels.calendarAdd;
  return (
    <button
      type="button"
      className={`tdp-row-cal${calendar.added ? " is-added" : ""}`}
      aria-label={label}
      title={label}
      aria-pressed={!!calendar.added}
      aria-busy={!!calendar.loading}
      disabled={!!calendar.loading}
      onClick={(e) => {
        e.stopPropagation();
        if (onClick) onClick();
      }}
    >
      {calendar.loading ? (
        <ProSpinner small />
      ) : (
        <MaterialIcon icon={calendar.added ? "event_available" : "calendar_add_on"} size={20} fill={calendar.added ? 1 : 0} />
      )}
    </button>
  );
}

/**
 * Task row as on mobile: the check left, the caption (project · Termin / Frist), the text (struck
 * through when done), then the tags, the day chip and the time — or, for a bookmark, "Ohne Datum"
 * with "Termin zuweisen". The assignees' progress is on the details page only. `compact` = the room
 * tab row (text, due, chevron).
 * r = { todo, check, finished, overdue, caption, eyebrow, title, due, tags, dayChip, time, undated, state, calendar }.
 * `onSchedule(todo, "YYYY-MM-DD")` gives an undated row its day; `onCalendar(todo)` is the
 * calendar button (shown while `r.calendar` is set).
 */
export function TaskRow({ r, compact, labels = {}, onOpen, onCheck, onSchedule, onCalendar }) {
  if (!r || !r.todo) return null;
  const { todo } = r;
  const state = r.state || {};
  const { selectMode, selected, selectDisabled, busy, active, highlighted } = state;
  const check = selectMode ? null : r.check;
  const finished = !!r.finished;
  const lateTone = r.overdue && !finished;
  const checkLabel = { open: labels.markDone, done: labels.markOpen, confirm: labels.verify }[check];
  const cls = [
    "tdp-row",
    compact ? "tdp-row--compact" : "",
    active ? "is-active" : "",
    highlighted ? "is-highlighted" : "",
    selectMode === "delete" && selected ? "is-del-selected" : "",
    selectMode === "adjust" && selected ? "is-adj-selected" : "",
    selectMode && selectDisabled ? "is-dimmed" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const open = () => {
    if (onOpen) onOpen(todo);
  };
  const undated = !!r.undated && !selectMode;
  return (
    <div
      className={cls}
      role="button"
      tabIndex={0}
      data-todo-id={todo.todoId}
      aria-pressed={selectMode ? !!selected : undefined}
      onClick={open}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
    >
      <TodoCheck
        check={check}
        finished={finished}
        busy={busy}
        selectMode={selectMode}
        selected={selected}
        selectDisabled={selectDisabled}
        onClick={() => onCheck && onCheck(todo, check)}
        label={checkLabel}
        compact={compact}
      />
      {compact ? (
        <div className="tdp-row-body tdp-row-body--compact">
          <div className={`tdp-row-title${finished ? " is-finished" : ""}`}>{r.title}</div>
          {r.due ? <div className={`tdp-row-duetext${lateTone ? " is-overdue" : ""}`}>{r.due}</div> : null}
        </div>
      ) : (
        <div className="tdp-row-body">
          {r.eyebrow !== undefined ? (r.eyebrow ? <div className="tdp-row-room">{r.eyebrow}</div> : null) : <div className="tdp-row-room">{r.caption}</div>}
          <div className={`tdp-row-title${finished ? " is-finished" : ""}`}>{r.title}</div>
          {undated ? (
            // A bookmark: "Ohne Datum" and, on the same line, "Termin zuweisen".
            <div className="tdp-row-meta tdp-row-bmmeta">
              <span className="tdp-due">
                <MaterialIcon icon="event_busy" size={16} />
                <span className="tdp-due-text">{r.due}</span>
              </span>
              {onSchedule ? <AssignDate label={labels.assignDate} busy={busy} onPick={(day) => onSchedule(todo, day)} /> : null}
            </div>
          ) : (r.tags && r.tags.length) || r.dayChip || r.time ? (
            <div className="tdp-row-meta">
              {(r.tags || []).map((g) => (
                <TodoTag key={g.key} label={g.label} tone={g.tone} />
              ))}
              {r.dayChip ? <TodoTag key={r.dayChip.key} label={r.dayChip.label} tone={r.dayChip.tone} /> : null}
              {r.time ? (
                <span className={`tdp-due${lateTone ? " is-overdue" : ""}`}>
                  <MaterialIcon icon="schedule" size={16} />
                  <span className="tdp-due-text">{r.time}</span>
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
      {r.calendar && onCalendar && !selectMode ? (
        <CalendarButton calendar={r.calendar} labels={labels} onClick={() => onCalendar(todo)} />
      ) : null}
      {compact ? <MaterialIcon icon="keyboard_arrow_right" size={20} className="tdp-row-chev" /> : null}
    </div>
  );
}

/** Collapsible section caption: dot, title, count and a chevron turned -90° when closed. */
export function SectionHead({ title, count, tone, open, onToggle, first, action }) {
  if (action) {
    // A header with its own action ("Alle löschen"): the title still opens and closes.
    return (
      <div className={`tdp-sec-head tdp-sec-head--action${first ? " is-first" : ""}${open ? " is-open" : ""}`}>
        <button type="button" className="tdp-sec-toggle" aria-expanded={!!open} onClick={onToggle}>
          <span className={`tdp-sec-dot tdp-sec-dot--${tone}`} />
          <span className="tdp-caption tdp-sec-title">{title}</span>
          <span className="tdp-sec-count">{count}</span>
        </button>
        <button type="button" className="tdp-sec-action" onClick={action.onPress} disabled={!!action.busy} title={action.label}>
          {action.busy ? <ProSpinner small /> : <MaterialIcon icon={action.icon || "delete_sweep"} size={16} />}
          <span>{action.label}</span>
        </button>
        <MaterialIcon icon="keyboard_arrow_down" size={20} className="tdp-sec-chev" onClick={onToggle} />
      </div>
    );
  }
  return (
    <button
      type="button"
      className={`tdp-sec-head${first ? " is-first" : ""}${open ? " is-open" : ""}`}
      aria-expanded={!!open}
      onClick={onToggle}
    >
      <span className={`tdp-sec-dot tdp-sec-dot--${tone}`} />
      <span className="tdp-caption tdp-sec-title">{title}</span>
      <span className="tdp-sec-count">{count}</span>
      <span className="tdp-flex" />
      <MaterialIcon icon="keyboard_arrow_down" size={20} className="tdp-sec-chev" />
    </button>
  );
}

/* ---- Plan card (list header) ------------------------------------------------------- */

/** Segmented switch (kj-tab items). items = [{ key, label, icon, leading, onPress }]. */
export function Segment({ items = [], value, onChange, className = "" }) {
  return (
    <div className={`tdp-seg${className ? ` ${className}` : ""}`} role="tablist">
      {items.map((it) => {
        const on = it.key === value;
        return (
          <button
            key={it.key}
            type="button"
            role="tab"
            aria-selected={on}
            className={`kj-tab tdp-seg-item${on ? " is-active" : ""}`}
            onClick={() => {
              if (it.onPress) {
                it.onPress();
                return;
              }
              if (!on && onChange) onChange(it.key);
            }}
          >
            {it.leading ? it.leading : it.icon ? <MaterialIcon icon={it.icon} size={17} fill={on ? 1 : 0} /> : null}
            <span className="tdp-seg-label">{it.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** A small popover with the browser's date field: pick any day (Cancel · Show day). */
export function DayPickPopover({ open, value, title, cancelLabel, doneLabel, onPick, onClose, align = "right" }) {
  const [temp, setTemp] = useState(value || "");
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (open) setTemp(value || "");
  }, [open, value]);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target) && closeRef.current) closeRef.current();
    };
    const onKey = (e) => {
      if (e.key === "Escape" && closeRef.current) closeRef.current();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  if (!open) return null;
  return (
    <div ref={ref} className={`tdp-daypick tdp-daypick--${align}`} role="dialog" aria-label={title}>
      <div className="tdp-daypick-title">{title}</div>
      <input type="date" className="tdp-input" value={temp} onChange={(e) => setTemp(e.target.value)} autoFocus />
      <div className="tdp-daypick-actions">
        <button type="button" className="kj-btn kj-btn--secondary kj-btn--sm" onClick={onClose}>
          {cancelLabel}
        </button>
        <button
          type="button"
          className="kj-btn kj-btn--primary kj-btn--sm"
          disabled={!temp}
          onClick={() => {
            if (temp && onPick) onPick(temp);
            if (onClose) onClose();
          }}
        >
          {doneLabel}
        </button>
      </div>
    </div>
  );
}

/**
 * The list header card: the All / My day switch (My day reads as its date) with the date
 * picker beside it, and a footer with My day's progress ("2 of 5 completed") and "Adjust
 * list". The switch is its own sticky slab so it stays reachable while the list scrolls
 * (All jumps to today / the first unseen ToDo).
 *   pick { label, onPick, picker } · progress { label, value } · adjust { label, onPress }
 */
export function PlanCard({ seg, pick, progress, adjust }) {
  const footer = progress || adjust;
  return (
    <>
      <div className="tdp-plan-pin">
        <div className={`tdp-plan tdp-plan--seg${footer ? "" : " is-alone"}`}>
          <div className={`tdp-plan-seg${pick ? " tdp-plan-seg--pick" : ""}`}>
            <Segment items={seg.items} value={seg.value} onChange={seg.onChange} />
            {pick ? (
              <span className="tdp-anchor">
                <button type="button" className="tdp-seg-pick" onClick={pick.onPick} aria-label={pick.label} title={pick.label}>
                  <MaterialIcon icon="calendar_month" size={20} />
                </button>
                {pick.picker}
              </span>
            ) : null}
          </div>
        </div>
      </div>
      {footer ? (
        <div className="tdp-plan tdp-plan--body">
          <div className="tdp-plan-foot">
            {progress ? (
              <div className="tdp-plan-prog">
                <span className="tdp-plan-prog-label">{progress.label}</span>
                <progress className="tdp-progress tdp-progress--ok tdp-plan-bar" value={progress.value} max={1} />
              </div>
            ) : (
              <span className="tdp-flex" />
            )}
            {adjust ? (
              <button type="button" className="tdp-link-btn tdp-plan-adjust" onClick={adjust.onPress}>
                <MaterialIcon icon="checklist" size={18} />
                <span>{adjust.label}</span>
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}

/* ---- States ----------------------------------------------------------------------- */

/** Empty state: icon in a soft circle, title, hint and an optional button. `card` = inside a card. */
export function TodoEmpty({ icon, tone, title, hint, action, onAction, card }) {
  return (
    <div className={`tdp-empty${card ? " tdp-empty--card" : ""}`}>
      {icon ? (
        <span className={`tdp-empty-icon${tone ? ` tdp-empty-icon--${tone}` : ""}`}>
          <MaterialIcon icon={icon} size={28} />
        </span>
      ) : null}
      {title ? <div className="tdp-empty-title">{title}</div> : null}
      {hint ? <div className="tdp-empty-hint">{hint}</div> : null}
      {action ? (
        <button type="button" className="kj-btn kj-btn--primary kj-btn--sm tdp-empty-action" onClick={onAction}>
          {action}
        </button>
      ) : null}
    </div>
  );
}

/** First load: a caption bone and one card of bone rows. */
export function ListSkeleton({ rows = 4 }) {
  return (
    <div className="tdp-skel" aria-hidden="true">
      <span className="tdp-bone tdp-bone--caption" />
      <div className="tdp-card">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="tdp-skel-row">
            <span className="tdp-bone tdp-bone--circle" />
            <div className="tdp-skel-text">
              <span className="tdp-bone tdp-bone--w45" />
              <span className="tdp-bone tdp-bone--w90 tdp-bone--tall" />
              <span className="tdp-bone tdp-bone--w55" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Sticky bar at the bottom of the list panel (delete mode, My day adjust). */
export function ModeBar({ children }) {
  return <div className="tdp-modebar">{children}</div>;
}

/* ---- Modal / sheets ------------------------------------------------------------------- */

// Only the top-most open modal reacts to Escape.
const modalStack = [];

/** Centered modal with a title row (close ×), a scrolling body and an optional footer. */
export function ProModal({ open, title, onClose, closeLabel, children, footer, className = "", wide }) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    const id = {};
    modalStack.push(id);
    const onKey = (e) => {
      if (e.key !== "Escape" || modalStack[modalStack.length - 1] !== id) return;
      e.stopPropagation();
      if (closeRef.current) closeRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const i = modalStack.indexOf(id);
      if (i >= 0) modalStack.splice(i, 1);
    };
  }, [open]);
  if (!open) return null;
  return ReactDOM.createPortal(
    <div
      className="tdp tdp-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && closeRef.current) closeRef.current();
      }}
    >
      <div className={`tdp-modal${wide ? " tdp-modal--wide" : ""}${className ? ` ${className}` : ""}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="tdp-modal-head">
          <div className="tdp-modal-title" role="heading" aria-level={3}>
            {title}
          </div>
          <button type="button" className="tdp-modal-close" onClick={onClose} aria-label={closeLabel} title={closeLabel}>
            <MaterialIcon icon="close" size={18} />
          </button>
        </div>
        <div className="tdp-modal-body">{children}</div>
        {footer ? <div className="tdp-modal-foot">{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
}

const foldText = (value) =>
  String(value == null ? "" : value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

/**
 * Searchable pick list in a modal. items = [{ key, title, sub, avatar: { name, src, kind, code } }].
 * Single: a click is onChange(key) and closes; allOption { key, title, icon } is a first row.
 * Multiple: select circles and a footer "Clear" · "Apply (n)" → onChange(keys) and close.
 */
export function SelectSheet({
  open,
  title,
  items = [],
  value,
  multiple,
  allOption,
  onChange,
  onClose,
  loading,
  searchPlaceholder,
  emptyText,
  applyLabel,
  clearLabel,
  closeLabel,
}) {
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState([]);
  useEffect(() => {
    if (!open) return;
    setQ("");
    setDraft(multiple ? (Array.isArray(value) ? value.map(String) : []) : []);
    // Only on open: the draft is the user's work while the sheet is up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const shown = useMemo(() => {
    const f = foldText(q.trim());
    if (!f) return items;
    return items.filter((it) => foldText(`${it.title || ""} ${it.sub || ""}`).includes(f));
  }, [items, q]);
  const isOn = (key) => (multiple ? draft.indexOf(String(key)) >= 0 : value != null && value !== "" && String(value) === String(key));
  const pick = (key) => {
    if (multiple) {
      const k = String(key);
      setDraft((d) => (d.indexOf(k) >= 0 ? d.filter((x) => x !== k) : [...d, k]));
      return;
    }
    if (onChange) onChange(key);
    if (onClose) onClose();
  };
  const apply = () => {
    const keys = draft.map((k) => {
      const it = items.find((i) => String(i.key) === k);
      return it ? it.key : k;
    });
    if (onChange) onChange(keys);
    if (onClose) onClose();
  };
  const noValue = value == null || value === "";
  const query = q.trim();
  let empty = null;
  if (!loading && !shown.length) {
    empty = typeof emptyText === "function" ? emptyText(query) : emptyText;
  }
  const footer = multiple ? (
    <div className="tdp-btn-row">
      <button type="button" className="kj-btn kj-btn--secondary tdp-btn-flex" onClick={() => setDraft([])} disabled={!draft.length}>
        {clearLabel}
      </button>
      <button type="button" className="kj-btn kj-btn--primary tdp-btn-flex" onClick={apply}>
        {draft.length ? `${applyLabel} (${draft.length})` : applyLabel}
      </button>
    </div>
  ) : null;
  return (
    <ProModal open={open} title={title} onClose={onClose} closeLabel={closeLabel} footer={footer} className="tdp-select">
      <ProSearchField value={q} onChange={setQ} placeholder={searchPlaceholder} clearLabel={clearLabel} autoFocus />
      <div className="tdp-card tdp-select-list">
        {allOption && !query ? (
          <button type="button" className="tdp-select-row" onClick={() => pick(allOption.key)}>
            <span className="tdp-select-lead">
              <MaterialIcon icon={allOption.icon || "block"} size={22} />
            </span>
            <span className="tdp-select-text">
              <span className="tdp-select-title">{allOption.title}</span>
            </span>
            {noValue ? <MaterialIcon icon="check" size={22} weight={600} className="tdp-select-mark" /> : <span className="tdp-select-slot" />}
          </button>
        ) : null}
        {shown.map((it) => {
          const on = isOn(it.key);
          return (
            <button key={String(it.key)} type="button" className={`tdp-select-row${on ? " is-on" : ""}`} onClick={() => pick(it.key)} role={multiple ? "checkbox" : "radio"} aria-checked={on}>
              {it.avatar ? <ProAvatar size={36} {...it.avatar} /> : null}
              <span className="tdp-select-text">
                <span className="tdp-select-title">{it.title}</span>
                {it.sub ? <span className="tdp-select-sub">{it.sub}</span> : null}
              </span>
              {multiple ? (
                <span className={`tdp-select-circle${on ? " is-selected" : ""}`}>{on ? <MaterialIcon icon="check" size={14} weight={700} /> : null}</span>
              ) : on ? (
                <MaterialIcon icon="check" size={22} weight={600} className="tdp-select-mark" />
              ) : (
                <span className="tdp-select-slot" />
              )}
            </button>
          );
        })}
        {loading && !shown.length
          ? [0, 1, 2].map((i) => (
              <div key={`bone-${i}`} className="tdp-select-row tdp-select-row--bone">
                <span className="tdp-bone tdp-bone--avatar" />
                <span className="tdp-bone tdp-bone--w55" />
              </div>
            ))
          : null}
        {empty ? <div className="tdp-select-empty">{empty}</div> : null}
      </div>
    </ProModal>
  );
}

/** People to pick from one by one (move a user back to in progress). */
export function PeoplePickSheet({ open, title, users = [], busyUserId, onPick, onClose, closeLabel }) {
  return (
    <ProModal open={open} title={title} onClose={onClose} closeLabel={closeLabel} className="tdp-select">
      <div className="tdp-card">
        {users.map((u) => (
          <button key={String(u.id)} type="button" className="tdp-person tdp-person--button" onClick={() => onPick && onPick(u.id)} disabled={!!busyUserId}>
            <ProAvatar name={u.fullName || u.name} src={u.src} size={38} />
            <span className="tdp-person-name tdp-flex">{u.name}</span>
            {busyUserId === u.id ? <ProSpinner small /> : <MaterialIcon icon="replay" size={22} className="tdp-accent-text" />}
          </button>
        ))}
      </div>
    </ProModal>
  );
}
